import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete } from '@/utils/api';
import { useToast } from '@/components/Toast';
import SavingsStreak from '@/components/SavingsStreak';
import StreakNotificationSettings from '@/components/StreakNotificationSettings';

// Category display config
const CATEGORY_CONFIG = {
  debt: { icon: '🎯', label: 'Debt Payoff', color: 'blue', bgColor: 'bg-blue-50', borderColor: 'border-blue-200', textColor: 'text-blue-600' },
  savings: { icon: '💰', label: 'Savings Stash', color: 'green', bgColor: 'bg-green-50', borderColor: 'border-green-200', textColor: 'text-green-600' },
  invest: { icon: '📈', label: 'Investments', color: 'purple', bgColor: 'bg-purple-50', borderColor: 'border-purple-200', textColor: 'text-purple-600' },
  enjoy: { icon: '🎉', label: 'Fun Money', color: 'yellow', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200', textColor: 'text-yellow-600' },
};

// Progress Ring Component
function ProgressRing({ progress, size = 120, strokeWidth = 10, color = 'blue' }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (Math.min(progress, 100) / 100) * circumference;
  
  const colorMap = {
    blue: '#3B82F6',
    green: '#10B981',
    purple: '#8B5CF6',
    yellow: '#F59E0B',
  };

  return (
    <svg width={size} height={size} className="transform -rotate-90" role="img" aria-label={`Progress: ${Math.round(progress)}%`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#E5E7EB"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={colorMap[color] || colorMap.blue}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-500"
      />
    </svg>
  );
}

// Summary Card Component - Compact
function SummaryCard({ icon, title, amount, subtitle, color }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-3`} role="region" aria-label={title}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl" aria-hidden="true">{icon}</span>
        <h3 className="font-semibold text-gray-700 text-sm">{title}</h3>
      </div>
      <p className={`text-2xl font-bold ${color}`}>${amount.toLocaleString()}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
}

// Calculate weeks until target date
function getWeeksUntilDate(targetDate) {
  if (!targetDate) return null;
  const now = new Date();
  const target = new Date(targetDate);
  const diffTime = target - now;
  const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
  return diffWeeks > 0 ? diffWeeks : null;
}

// Goal Card Component
function GoalCard({ goal, onEdit, onDelete, onAddFunds, onWithdraw }) {
  const progress = goal.target_amount > 0 ? (goal.current_amount / goal.target_amount) * 100 : 0;
  const remaining = goal.target_amount - goal.current_amount;
  
  const weeksUntil = getWeeksUntilDate(goal.target_date);
  const weeklyNeeded = weeksUntil && remaining > 0 ? (remaining / weeksUntil).toFixed(2) : null;
  
  return (
    <article className="bg-white rounded-xl shadow-sm border border-gray-200 p-5" data-testid={`goal-card-${goal.id}`} aria-label={`Goal: ${goal.name}`}>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="font-bold text-lg text-gray-900">{goal.name}</h3>
          {goal.target_date && (
            <p className="text-sm text-gray-500">
              Target: {new Date(goal.target_date).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(goal)}
            className="text-sm text-blue-600 hover:text-blue-800"
            aria-label={`Edit goal ${goal.name}`}
            data-testid={`edit-goal-btn-${goal.id}`}
          >
            Edit
          </button>
          <button
            onClick={() => onDelete(goal.id)}
            className="text-sm text-red-600 hover:text-red-800"
            aria-label={`Delete goal ${goal.name}`}
            data-testid={`delete-goal-btn-${goal.id}`}
          >
            Delete
          </button>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="relative">
          <ProgressRing progress={progress} size={80} strokeWidth={8} color="green" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-bold text-gray-700">{Math.round(progress)}%</span>
          </div>
        </div>
        
        <div className="flex-1">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Current</span>
            <span className="font-semibold text-green-600">${goal.current_amount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Target</span>
            <span className="font-semibold text-gray-900">${goal.target_amount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Remaining</span>
            <span className="font-semibold text-blue-600">${remaining.toLocaleString()}</span>
          </div>
        </div>
      </div>
      
      {/* Weekly Savings Calculator */}
      {weeklyNeeded && remaining > 0 && (
        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2">
            <span className="text-blue-500">📅</span>
            <p className="text-sm text-blue-800">
              Save <span className="font-bold">${weeklyNeeded}/week</span> to reach your goal in{' '}
              <span className="font-bold">{weeksUntil} weeks</span>
            </p>
          </div>
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="mt-4 flex gap-2">
        <button
          onClick={() => onAddFunds(goal)}
          className="flex-1 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
          data-testid={`add-funds-btn-${goal.id}`}
        >
          + Add Funds
        </button>
        {goal.current_amount > 0 && (
          <button
            onClick={() => onWithdraw(goal)}
            className="flex-1 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
            data-testid={`withdraw-btn-${goal.id}`}
          >
            - Withdraw
          </button>
        )}
      </div>
    </article>
  );
}

// Allocation History Item
function AllocationItem({ allocation, onDelete }) {
  const config = CATEGORY_CONFIG[allocation.category] || CATEGORY_CONFIG.savings;
  const date = new Date(allocation.created_at).toLocaleDateString();
  
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg ${config.bgColor} ${config.borderColor} border`}>
      <div className="flex items-center gap-3">
        <span className="text-xl">{config.icon}</span>
        <div>
          <p className="font-medium text-gray-900">{config.label}</p>
          <p className="text-xs text-gray-500">{date}{allocation.note && ` • ${allocation.note}`}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className={`font-bold ${config.textColor}`}>${allocation.amount.toFixed(2)}</span>
        <button
          onClick={() => onDelete(allocation.id)}
          className="text-gray-400 hover:text-red-600 text-sm"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function Goals() {
  const { toast } = useToast();
  const [allocations, setAllocations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [showAddFundsModal, setShowAddFundsModal] = useState(null);
  const [showWithdrawModal, setShowWithdrawModal] = useState(null);
  const [addFundsAmount, setAddFundsAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [fundsMovedConfirmed, setFundsMovedConfirmed] = useState(false);
  const [withdrawConfirmed, setWithdrawConfirmed] = useState(false);
  const [goalForm, setGoalForm] = useState({
    name: '',
    target_amount: '',
    current_amount: '',
    target_date: '',
  });

  const loadData = useCallback(async () => {
    try {
      const [allocRes, summaryRes, goalsRes] = await Promise.all([
        apiGet('/allocations'),
        apiGet('/allocations/summary'),
        apiGet('/goals'),
      ]);

      if (allocRes.ok) {
        const data = await allocRes.json();
        setAllocations(data);
      }
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }
      if (goalsRes.ok) {
        const data = await goalsRes.json();
        // Handle both array response and object with goals property
        setGoals(Array.isArray(data) ? data : (data.goals || []));
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeleteAllocation = async (id) => {
    if (!window.confirm('Delete this allocation record?')) return;
    
    try {
      const response = await apiDelete(`/allocations/${id}`);
      
      if (response.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error deleting allocation:', error);
    }
  };

  const handleSaveGoal = async (e) => {
    e.preventDefault();
    
    const goalData = {
      name: goalForm.name,
      target_amount: parseFloat(goalForm.target_amount) || 0,
      current_amount: parseFloat(goalForm.current_amount) || 0,
      target_date: goalForm.target_date ? new Date(goalForm.target_date).toISOString() : null,
    };
    
    try {
      const response = editingGoal 
        ? await apiPut(`/goals/${editingGoal.id}`, goalData)
        : await apiPost('/goals', goalData);
      
      if (response.ok) {
        loadData();
        resetGoalForm();
        toast.success(editingGoal ? 'Goal updated successfully!' : 'Goal created successfully!');
      }
    } catch (error) {
      console.error('Error saving goal:', error);
      toast.error('Failed to save goal. Please try again.');
    }
  };

  const handleDeleteGoal = async (id) => {
    if (!window.confirm('Delete this savings goal?')) return;
    
    try {
      const response = await apiDelete(`/goals/${id}`);
      
      if (response.ok) {
        loadData();
        toast.success('Goal deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error('Failed to delete goal.');
    }
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setGoalForm({
      name: goal.name,
      target_amount: goal.target_amount.toString(),
      current_amount: goal.current_amount.toString(),
      target_date: goal.target_date ? goal.target_date.split('T')[0] : '',
    });
    setShowGoalForm(true);
  };

  const handleAddFunds = async () => {
    if (!showAddFundsModal || !addFundsAmount) return;
    
    try {
      const response = await apiPost(`/goals/${showAddFundsModal.id}/add-funds?amount=${parseFloat(addFundsAmount)}`, {});
      
      if (response.ok) {
        loadData();
        setShowAddFundsModal(null);
        setAddFundsAmount('');
        setFundsMovedConfirmed(false);
        toast.success('Funds added successfully!');
      }
    } catch (error) {
      console.error('Error adding funds:', error);
      toast.error('Failed to add funds.');
    }
  };

  const handleWithdraw = async () => {
    if (!showWithdrawModal || !withdrawAmount) return;
    
    const amount = parseFloat(withdrawAmount);
    if (amount > showWithdrawModal.current_amount) {
      toast.error('Cannot withdraw more than current balance');
      return;
    }
    
    try {
      const response = await apiPost(`/goals/${showWithdrawModal.id}/withdraw?amount=${amount}`, {});
      
      if (response.ok) {
        loadData();
        setShowWithdrawModal(null);
        setWithdrawAmount('');
        setWithdrawConfirmed(false);
        toast.success('Withdrawal successful!');
      }
    } catch (error) {
      console.error('Error withdrawing:', error);
      toast.error('Failed to withdraw funds.');
    }
  };

  const resetGoalForm = () => {
    setShowGoalForm(false);
    setEditingGoal(null);
    setGoalForm({ name: '', target_amount: '', current_amount: '', target_date: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your progress...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="space-y-4 max-w-7xl mx-auto" data-testid="goals-page" role="main" aria-label="Goals and Allocations">
      {/* Header - Compact */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-4 text-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold" data-testid="goals-title">🎯 Stacks and Goals</h1>
            <p className="text-blue-100 text-sm">
              Track allocations • <Link to="/dashboard" className="underline font-semibold text-white/90 hover:text-white">Allocate from Dashboard</Link>
            </p>
          </div>
        </div>
      </header>

      {/* Savings Streak Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <SavingsStreak className="w-full" />
        </div>
        <div>
          <StreakNotificationSettings className="w-full" />
        </div>
      </div>

      {/* Summary Cards - More Compact */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" aria-label="Allocation summary">
        <SummaryCard
          icon="💰"
          title="Total Allocated"
          amount={summary?.grand_total || 0}
          subtitle={`${summary?.allocation_count || 0} allocations`}
          color="text-gray-900"
        />
        <SummaryCard
          icon="🎯"
          title="To Debt"
          amount={summary?.category_totals?.debt || 0}
          subtitle={summary?.debt_impact?.estimated_interest_saved > 0 
            ? `~$${summary.debt_impact.estimated_interest_saved.toFixed(0)} interest saved`
            : 'Attacking your debt'}
          color="text-blue-600"
        />
        <SummaryCard
          icon="💰"
          title="To Savings"
          amount={summary?.category_totals?.savings || 0}
          subtitle={`${summary?.savings_progress?.active_goals || 0} active ${(summary?.savings_progress?.active_goals || 0) === 1 ? 'goal' : 'goals'}`}
          color="text-green-600"
        />
        <SummaryCard
          icon="📈"
          title="To Investments"
          amount={summary?.category_totals?.invest || 0}
          subtitle="Growing your wealth"
          color="text-purple-600"
        />
      </section>

      {/* Category Distribution Chart - Compact */}
      {summary?.grand_total > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h2 className="text-base font-semibold text-gray-900 mb-3">📊 Where Your Money Went</h2>
          <div className="flex flex-wrap gap-4 items-center justify-center">
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
              const amount = summary?.category_totals?.[key] || 0;
              const percent = summary?.grand_total > 0 ? (amount / summary.grand_total) * 100 : 0;
              
              if (amount === 0) return null;
              
              return (
                <div key={key} className="text-center">
                  <div className="relative inline-block">
                    <ProgressRing progress={percent} size={100} strokeWidth={12} color={config.color} />
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-xl">{config.icon}</span>
                      <span className="text-xs font-bold">{percent.toFixed(0)}%</span>
                    </div>
                  </div>
                  <p className="mt-2 font-medium text-gray-700">{config.label}</p>
                  <p className={`text-sm font-bold ${config.textColor}`}>${amount.toLocaleString()}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Savings Stash Quick Access - Compact */}
      <Link 
        to="/emergency-fund"
        className="block bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-3 hover:shadow-md transition-all group"
        data-testid="savings-stash-quick-access"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center text-lg shadow-lg">
              💰
            </div>
            <div>
              <h3 className="font-bold text-emerald-900 text-sm group-hover:text-emerald-700 transition-colors">
                Savings Stash
              </h3>
              <p className="text-xs text-emerald-600">
                Build your savings, secure your future
              </p>
            </div>
          </div>
          <div className="text-emerald-600 group-hover:translate-x-1 transition-transform">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </Link>

      {/* Savings Goals Section - Compact */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-base font-semibold text-gray-900">🎯 Savings Goals</h2>
          <button
            onClick={() => setShowGoalForm(true)}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg font-medium text-sm hover:bg-green-700 transition-colors"
            data-testid="add-goal-button"
          >
            + New Goal
          </button>
        </div>

        {/* Goal Form */}
        {showGoalForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-3">
              {editingGoal ? 'Edit Goal' : 'Create New Goal'}
            </h3>
            
            {/* Goal Examples */}
            {!editingGoal && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-2">💡 Goal Ideas:</p>
                <div className="flex flex-wrap gap-2">
                  {['Savings Stash', 'Vacation', 'New Car', 'House Down Payment', 'Wedding', 'Holiday Gifts', 'Tech Upgrade', 'Debt Payoff'].map((example) => (
                    <button
                      key={example}
                      type="button"
                      onClick={() => setGoalForm({ ...goalForm, name: example })}
                      className="px-3 py-1 text-xs bg-white hover:bg-blue-100 text-blue-700 rounded-full border border-blue-200 transition-colors"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <form onSubmit={handleSaveGoal} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Goal Name</label>
                <input
                  type="text"
                  required
                  value={goalForm.name}
                  onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
                  placeholder="e.g., Savings Stash, Vacation"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="goal-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Amount ($)</label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={goalForm.target_amount}
                  onChange={(e) => setGoalForm({ ...goalForm, target_amount: e.target.value })}
                  placeholder="5000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="goal-target-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Amount ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={goalForm.current_amount}
                  onChange={(e) => setGoalForm({ ...goalForm, current_amount: e.target.value })}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="goal-current-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Date (optional)</label>
                <input
                  type="date"
                  value={goalForm.target_date}
                  onChange={(e) => setGoalForm({ ...goalForm, target_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="goal-date-input"
                />
              </div>
              <div className="md:col-span-2 flex gap-3">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                  data-testid="save-goal-button"
                >
                  {editingGoal ? 'Update Goal' : 'Create Goal'}
                </button>
                <button
                  type="button"
                  onClick={resetGoalForm}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Goals Grid */}
        {goals.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-5xl mb-3">🎯</div>
            <p className="text-gray-600">No savings goals yet</p>
            <p className="text-sm text-gray-500">Create a goal to start tracking your progress!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {goals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                onEdit={handleEditGoal}
                onDelete={handleDeleteGoal}
                onAddFunds={setShowAddFundsModal}
                onWithdraw={setShowWithdrawModal}
              />
            ))}
          </div>
        )}
      </div>

      {/* Allocation History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">📜 Recent Allocations</h2>
        
        {allocations.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-5xl mb-3">💸</div>
            <p className="text-gray-600">No allocations yet</p>
            <p className="text-sm text-gray-500">
              Use the Action Engine on the Dashboard to allocate your leftover funds
            </p>
            <Link
              to="/dashboard"
              className="inline-block mt-4 px-6 py-2 bg-gradient-to-r from-emerald-500 to-purple-600 text-white rounded-lg font-medium hover:from-emerald-600 hover:to-purple-700 transition-all"
            >
              Go to Dashboard
            </Link>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {allocations.slice(0, 50).map((allocation) => (
              <AllocationItem
                key={allocation.id}
                allocation={allocation}
                onDelete={handleDeleteAllocation}
              />
            ))}
          </div>
        )}
      </div>

      {/* Monthly Breakdown */}
      {summary?.monthly_breakdown && Object.keys(summary.monthly_breakdown).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">📅 Monthly Breakdown</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-semibold text-gray-700">Month</th>
                  <th className="text-right py-2 px-3 font-semibold text-blue-600">Debt</th>
                  <th className="text-right py-2 px-3 font-semibold text-green-600">Savings</th>
                  <th className="text-right py-2 px-3 font-semibold text-purple-600">Invest</th>
                  <th className="text-right py-2 px-3 font-semibold text-yellow-600">Enjoy</th>
                  <th className="text-right py-2 px-3 font-semibold text-gray-900">Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(summary.monthly_breakdown).map(([month, data]) => (
                  <tr key={month} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium text-gray-900">{month}</td>
                    <td className="text-right py-2 px-3 text-blue-600">${data.debt.toFixed(0)}</td>
                    <td className="text-right py-2 px-3 text-green-600">${data.savings.toFixed(0)}</td>
                    <td className="text-right py-2 px-3 text-purple-600">${data.invest.toFixed(0)}</td>
                    <td className="text-right py-2 px-3 text-yellow-600">${data.enjoy.toFixed(0)}</td>
                    <td className="text-right py-2 px-3 font-bold text-gray-900">${data.total.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Funds Modal */}
      {showAddFundsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Add Funds to {showAddFundsModal.name}
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={addFundsAmount}
                onChange={(e) => setAddFundsAmount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="100"
                autoFocus
              />
            </div>
            
            {/* Money Movement Confirmation */}
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={fundsMovedConfirmed}
                  onChange={(e) => setFundsMovedConfirmed(e.target.checked)}
                  className="mt-1 w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  data-testid="funds-moved-checkbox"
                />
                <div>
                  <span className="font-medium text-gray-900">I've moved this money</span>
                  <p className="text-sm text-gray-600 mt-0.5">
                    I confirm I've transferred ${addFundsAmount || '0'} to my savings account or investment platform.
                  </p>
                </div>
              </label>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleAddFunds}
                disabled={!fundsMovedConfirmed}
                className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                  fundsMovedConfirmed
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Add Funds
              </button>
              <button
                onClick={() => {
                  setShowAddFundsModal(null);
                  setAddFundsAmount('');
                  setFundsMovedConfirmed(false);
                }}
                className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Withdraw from {showWithdrawModal.name}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Available balance: <span className="font-bold text-green-600">${showWithdrawModal.current_amount.toLocaleString()}</span>
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
              <input
                type="number"
                min="0"
                max={showWithdrawModal.current_amount}
                step="0.01"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="50"
                autoFocus
                data-testid="withdraw-amount-input"
              />
            </div>
            
            {/* Withdrawal Confirmation */}
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={withdrawConfirmed}
                  onChange={(e) => setWithdrawConfirmed(e.target.checked)}
                  className="mt-1 w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  data-testid="withdraw-confirm-checkbox"
                />
                <div>
                  <span className="font-medium text-gray-900">I've withdrawn this money</span>
                  <p className="text-sm text-gray-600 mt-0.5">
                    I confirm I've moved ${withdrawAmount || '0'} from my savings account.
                  </p>
                </div>
              </label>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleWithdraw}
                disabled={!withdrawConfirmed || !withdrawAmount || parseFloat(withdrawAmount) > showWithdrawModal.current_amount}
                className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                  withdrawConfirmed && withdrawAmount && parseFloat(withdrawAmount) <= showWithdrawModal.current_amount
                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                data-testid="confirm-withdraw-btn"
              >
                Withdraw
              </button>
              <button
                onClick={() => {
                  setShowWithdrawModal(null);
                  setWithdrawAmount('');
                  setWithdrawConfirmed(false);
                }}
                className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Goals;
